import { Check, ArrowRight, Phone } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import logo from "@/assets/sd-logo-transparent.webp";

const bgPanorama = "/assets/murtal-panorama.webp";

export const Hero = () => {
  return (
    <section
      id="hero"
      className="relative flex min-h-[100vh] items-center justify-center overflow-hidden bg-hero-gradient pt-20"
    >
      {/* Hintergrundbild – nur leicht weichgezeichnet, gut sichtbar */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ contain: "layout paint", willChange: "transform" }}
      >
        <img
          src={bgPanorama}
          alt=""
          aria-hidden
          width={1920}
          height={1080}
          decoding="async"
          loading="eager"
          fetchPriority="high"
          className="h-full w-full object-cover scale-105"
          style={{ filter: "blur(4px) saturate(1.05) brightness(0.72)" }}
        />
        {/* Leichte grüne Tönung + Lesbarkeits-Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, hsla(var(--primary)/0.55) 0%, hsla(var(--secondary)/0.45) 60%, hsla(var(--primary)/0.6) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, hsla(var(--primary)/0.15) 0%, hsla(var(--primary)/0.55) 100%)",
          }}
        />
      </div>


      <div className="container-narrow relative z-10 py-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mx-auto max-w-[820px] text-center"
        >
          <motion.img
            src={logo}
            alt="S&D Facility Service"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="mx-auto mb-4 h-36 w-auto drop-shadow-2xl md:h-44"
            width={240}
            height={240}
            loading="eager"
            // @ts-expect-error fetchpriority is a valid HTML attribute
            fetchpriority="high"
            decoding="async"
          />

          <span
            className="inline-flex items-center rounded-full border px-4 py-1.5"
            style={{
              background: "rgba(201,168,76,0.15)",
              borderColor: "#C9A84C",
              color: "#C9A84C",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            Ihr Facility-Partner im Murtal
          </span>

          <h1
            className="h1-display mt-5 text-white"
            style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}
          >
            {[
              "Ihr zuverlässiger Partner",
              "für Reinigung & Gartenpflege",
              "im Murtal",
            ].map((line, i) => (
              <motion.span
                key={line}
                custom={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 1,
                  delay: 0.5 + i * 0.2,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
                className="block bg-gradient-to-b from-white to-white/80 bg-clip-text text-transparent pb-1"
                style={{ lineHeight: 1.2 }}
              >
                {line}
              </motion.span>
            ))}
          </h1>

          <p
            className="mx-auto mt-4 max-w-2xl"
            style={{ color: "rgba(255,255,255,0.85)", fontSize: 17, lineHeight: 1.6 }}
          >
            Professionell. Pünktlich. Persönlich. Wir kümmern uns um Ihr Zuhause,
            Ihren Garten und Ihre Außenflächen — gründlich, fair und mit
            persönlicher Betreuung.
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/kontakt"
              className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-lg bg-accent px-8 py-4 text-[15px] font-semibold text-primary shadow-[0_10px_30px_-10px_hsla(var(--accent)/0.7)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-10px_hsla(var(--accent)/0.85)]"
              style={{ letterSpacing: "0.3px" }}
            >
              <Phone className="h-4 w-4" strokeWidth={2.5} />
              <span>Jetzt Kontakt aufnehmen</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
            </Link>
            <Link
              to="/leistungen"
              className="group inline-flex items-center justify-center gap-2 rounded-lg border-2 bg-white/10 px-8 py-4 text-[15px] font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-white/20"
              style={{
                borderColor: "rgba(255,255,255,0.6)",
                letterSpacing: "0.3px",
              }}
            >
              <span>Leistungen ansehen</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
            </Link>
          </div>

          <div className="mt-8">
            <div
              className="mx-auto mb-4 h-px w-24"
              style={{ background: "rgba(255,255,255,0.25)" }}
            />
            <ul className="flex flex-col items-center justify-center gap-4 text-white sm:flex-row sm:gap-10">
              {[
                "Gegründet 2024",
                "Gratis Erstbesichtigung",
                "Faire Preise",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-accent" strokeWidth={3} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
